/**
 * Client-side caller for the ARIA upload-time pre-processor.
 *
 * Two-step:
 *   1. Extract a 512 px JPEG thumbnail from the original File via
 *      <canvas> (images) or <video> (videos).
 *   2. POST the thumbnail to /api/admin/uploads/preview-analyze.
 *
 * Returns the parsed prediction object or null on any failure. Never
 * throws — the upload queue calls this in parallel with the actual
 * upload and the preview is a nice-to-have, never a blocker.
 */
import { extractPreviewThumbnail } from "@/lib/extractPreviewThumbnail";

export async function analyzeFilePreview({ api, file }) {
  if (!file) return null;
  const thumb = await extractPreviewThumbnail(file);
  if (!thumb) return null;
  // Defensive: backend caps thumbnails at 200 KB. Our canvas exporter
  // is tuned to ~50 KB at q=0.78 for 512 px — but a 4K still might
  // overshoot. Bail rather than waste a round trip on a rejection.
  if (thumb.size > 200 * 1024) return null;

  const fd = new FormData();
  fd.append("thumbnail", thumb, "thumb.jpg");
  try {
    const res = await api.post("/admin/uploads/preview-analyze", fd, {
      // Don't manually set Content-Type — axios derives the multipart
      // boundary automatically when given a FormData body. Setting it
      // by hand drops the boundary and some multipart parsers reject
      // the request.
      timeout: 20000, // model hard-cap on the server is 15s
    });
    const data = res.data || {};
    if (!data.available) return null;
    return {
      theme: data.theme,
      mood: data.mood,
      hashtags: data.hashtags || [],
      suggestedActions: data.suggested_actions || [],
      headline: data.headline || "",
      confidence: data.confidence || 0,
      cached: !!data.cached,
    };
  } catch {
    return null;
  }
}
